import React, { useState, useRef, useEffect, useCallback } from "react";
import MicIcon from "@mui/icons-material/Mic";
import MicOffIcon from "@mui/icons-material/MicOff";
import StopIcon from "@mui/icons-material/Stop";
import ArticleIcon from "@mui/icons-material/Article";
import SubjectIcon from "@mui/icons-material/Subject";
import LocalLibraryIcon from "@mui/icons-material/LocalLibrary";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import CircularProgress from "@mui/icons-material/RotateRight";
import RealtimeService, { ConnectionState, ActivityState } from "./realtimeService";

export default function VoiceAssistantDisplay({ companyId, storeId, onClose }) {
  const [connectionState, setConnectionState] = useState(ConnectionState.DISCONNECTED);
  const [activityState, setActivityState] = useState(ActivityState.IDLE);
  const [messages, setMessages] = useState([]);
  const [expandedSources, setExpandedSources] = useState({});
  const [isMuted, setIsMuted] = useState(false);
  const [error, setError] = useState(null);
  const [warning, setWarning] = useState(null);

  const serviceRef = useRef(null);
  const messagesEndRef = useRef(null);
  const pendingAssistantTextRef = useRef("");
  const pendingAnnotationsRef = useRef([]);
  const pendingUserMessageIdsRef = useRef([]);

  // Auto-scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Initialize service and auto-connect
  useEffect(() => {
    const service = new RealtimeService({
      onConnectionStateChange: (state) => {
        setConnectionState(state);
        if (state === ConnectionState.CONNECTED) {
          setError(null);
        }
        if (state === ConnectionState.CONNECTING || state === ConnectionState.DISCONNECTED) {
          pendingAssistantTextRef.current = "";
          pendingAnnotationsRef.current = [];
          // Remove any unresolved "..." placeholders from the message list
          const staleIds = pendingUserMessageIdsRef.current;
          if (staleIds.length > 0) {
            pendingUserMessageIdsRef.current = [];
            const staleSet = new Set(staleIds);
            setMessages((prev) => prev.filter((msg) => !staleSet.has(msg.id)));
          }
        }
      },
      onActivityStateChange: (state) => {
        setActivityState(state);
      },
      onSpeechStopped: () => {
        // Insert a placeholder user message immediately so it appears
        // before any assistant response. The transcript arrives async.
        const placeholderId = Date.now();
        pendingUserMessageIdsRef.current = [...pendingUserMessageIdsRef.current, placeholderId];
        setMessages((prev) => [
          ...prev,
          {
            id: placeholderId,
            content: "...",
            sender: "user",
            timestamp: new Date(),
            sources: [],
          },
        ]);
      },
      onUserTranscript: (text) => {
        const queue = pendingUserMessageIdsRef.current;
        const placeholderId = queue.length > 0 ? queue[0] : null;

        if (!text.trim()) {
          // Empty transcript (noise/transient) — remove the placeholder
          if (placeholderId) {
            pendingUserMessageIdsRef.current = queue.slice(1);
            setMessages((prev) => prev.filter((msg) => msg.id !== placeholderId));
          }
          return;
        }
        if (placeholderId) {
          // Update the oldest placeholder with the real transcript
          pendingUserMessageIdsRef.current = queue.slice(1);
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === placeholderId
                ? { ...msg, content: text }
                : msg
            )
          );
        } else {
          // No placeholder (edge case) — append normally
          setMessages((prev) => [
            ...prev,
            {
              id: Date.now(),
              content: text,
              sender: "user",
              timestamp: new Date(),
              sources: [],
            },
          ]);
        }
      },
      onAssistantTranscript: (text, isFinal) => {
        if (isFinal) {
          // Finalize the assistant message with accumulated annotations
          const finalText = text;
          const finalSources = pendingAnnotationsRef.current.map(formatAnnotation);
          pendingAssistantTextRef.current = "";
          pendingAnnotationsRef.current = [];

          setMessages((prev) => {
            // Replace the last partial assistant message or add new
            const lastMsg = prev[prev.length - 1];
            if (lastMsg && lastMsg.sender === "ai" && lastMsg.isPartial) {
              return [
                ...prev.slice(0, -1),
                {
                  ...lastMsg,
                  content: finalText,
                  sources: finalSources,
                  isPartial: false,
                },
              ];
            }
            return [
              ...prev,
              {
                id: Date.now(),
                content: finalText,
                sender: "ai",
                timestamp: new Date(),
                sources: finalSources,
                isPartial: false,
              },
            ];
          });
        } else {
          // Partial — accumulate delta
          pendingAssistantTextRef.current += text;
          const accumulated = pendingAssistantTextRef.current;

          setMessages((prev) => {
            const lastMsg = prev[prev.length - 1];
            if (lastMsg && lastMsg.sender === "ai" && lastMsg.isPartial) {
              return [
                ...prev.slice(0, -1),
                { ...lastMsg, content: accumulated },
              ];
            }
            return [
              ...prev,
              {
                id: Date.now(),
                content: accumulated,
                sender: "ai",
                timestamp: new Date(),
                sources: [],
                isPartial: true,
              },
            ];
          });
        }
      },
      onAnnotations: (annotations) => {
        pendingAnnotationsRef.current = [
          ...pendingAnnotationsRef.current,
          ...annotations,
        ];
      },
      onError: (msg) => {
        setError(msg);
      },
      onWarning: (msg) => {
        setWarning(msg);
        // Auto-dismiss warning after 5 seconds
        setTimeout(() => setWarning(null), 5000);
      },
      onIdleTimeout: () => {
        setWarning("Session disconnected due to inactivity. Tap the microphone to reconnect.");
      },
    });

    serviceRef.current = service;
    service.connect(companyId, storeId);

    return () => {
      service.disconnect();
    };
  }, [companyId, storeId]);

  const formatAnnotation = (annotation) => ({
    id: annotation.uuid || Math.random().toString(),
    title: annotation.name || "Unknown document",
    path:
      (annotation.type === "FileUpload"
        ? annotation.presigned_url
        : annotation.pdf) || "/",
    extension: annotation.extension || "",
    fileSize: annotation.fileSize || "",
    type: annotation.type || "file",
  });

  const toggleSources = (messageId) => {
    setExpandedSources((prev) => ({
      ...prev,
      [messageId]: !prev[messageId],
    }));
  };

  const handleConnect = useCallback(() => {
    setError(null);
    serviceRef.current?.connect(companyId, storeId);
  }, [companyId, storeId]);

  const handleDisconnect = useCallback(() => {
    serviceRef.current?.disconnect();
  }, []);

  const handleToggleMute = useCallback(() => {
    const newMuted = !isMuted;
    setIsMuted(newMuted);
    serviceRef.current?.setMuted(newMuted);
  }, [isMuted]);

  const formatTimestamp = (timestamp) => {
    return new Intl.DateTimeFormat("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    }).format(timestamp);
  };

  const getStatusText = () => {
    if (connectionState === ConnectionState.CONNECTING) return "Connecting...";
    if (connectionState === ConnectionState.ERROR) return "Connection error";
    if (connectionState === ConnectionState.DISCONNECTED) return "Disconnected";
    switch (activityState) {
      case ActivityState.LISTENING:
        return "Listening...";
      case ActivityState.THINKING:
        return "Thinking...";
      case ActivityState.SPEAKING:
        return "Speaking...";
      default:
        return "Connected";
    }
  };

  const getStatusColor = () => {
    if (connectionState !== ConnectionState.CONNECTED) return "bg-gray-400";
    switch (activityState) {
      case ActivityState.LISTENING:
        return "bg-green-500";
      case ActivityState.THINKING:
        return "bg-yellow-500";
      case ActivityState.SPEAKING:
        return "bg-blue-500";
      default:
        return "bg-gray-400";
    }
  };

  const isConnected = connectionState === ConnectionState.CONNECTED;
  const isConnecting = connectionState === ConnectionState.CONNECTING;

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Warning banner */}
      {warning && (
        <div className="bg-yellow-50 border-b border-yellow-200 px-4 py-2 text-sm text-yellow-800 flex items-center justify-between">
          <span>{warning}</span>
          <button
            onClick={() => setWarning(null)}
            className="text-yellow-600 hover:text-yellow-800 ml-2"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Error banner */}
      {error && (
        <div className="bg-red-50 border-b border-red-200 px-4 py-2 text-sm text-red-700 flex items-center justify-between">
          <span>{error}</span>
          <button
            onClick={() => setError(null)}
            className="text-red-500 hover:text-red-700 ml-2"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Transcript area */}
      <div className="flex-1 overflow-y-auto p-4 bg-gray-50">
        {messages.length === 0 && isConnected && (
          <div className="text-center text-gray-400 mt-8">
            <MicIcon style={{ fontSize: 48 }} className="mb-2" />
            <p>Listening... ask me anything about your company documents.</p>
          </div>
        )}

        {messages.map((message) => (
          <div
            key={message.id}
            className={`mb-4 flex ${
              message.sender === "user" ? "justify-end" : "justify-start"
            }`}
          >
            <div
              className={`rounded-lg px-4 max-w-3/4 ${
                message.sender === "user"
                  ? "bg-violet-100 text-gray-700 py-3"
                  : "bg-white border border-gray-200 text-gray-700 pb-3 pt-3"
              }`}
            >
              <div className="flex flex-col">
                <div className="mb-1 text-sm">{message.content}</div>

                {/* PDF sources — shown inline by default */}
                {message.sender === "ai" &&
                  message.sources &&
                  message.sources.filter(
                    (s) =>
                      (s.extension?.toLowerCase() === "pdf" ||
                        s.type === "LearningSection") &&
                      s.path &&
                      s.path !== "/"
                  ).length > 0 && (
                    <div className="mt-3 border-t border-gray-100 pt-3">
                      {message.sources
                        .filter(
                          (s) =>
                            (s.extension?.toLowerCase() === "pdf" ||
                              s.type === "LearningSection") &&
                            s.path &&
                            s.path !== "/"
                        )
                        .map((source, index) => (
                          <div
                            key={source.id}
                            className={`${index > 0 ? "mt-3" : ""}`}
                          >
                            <div className="flex items-center space-x-2 p-1 text-xs">
                              <div className="rounded w-5 h-6 flex-shrink-0 flex items-center justify-center border border-gray-200 bg-white">
                                {source.type === "LearningSection" ? (
                                  <LocalLibraryIcon
                                    style={{ fontSize: "14px" }}
                                    className="text-gray-500"
                                  />
                                ) : (
                                  <SubjectIcon
                                    style={{ fontSize: "16px" }}
                                    className="text-gray-500"
                                  />
                                )}
                              </div>
                              <div className="flex-1 font-medium text-gray-700">
                                {source.title}
                              </div>
                              <a
                                href={source.path}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-violet-600 hover:text-violet-800 flex-shrink-0"
                                title="Open in new tab"
                              >
                                <ArticleIcon style={{ fontSize: "16px" }} />
                              </a>
                            </div>
                            <a
                              href={source.path}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="block mt-1.5 rounded border border-gray-200 overflow-hidden cursor-pointer hover:border-violet-300"
                              style={{ width: "100%", maxWidth: "400px" }}
                            >
                              <iframe
                                src={source.path + "#toolbar=0&navpanes=0"}
                                title={source.title}
                                className="w-full bg-white pointer-events-none"
                                style={{ aspectRatio: "8.5 / 11" }}
                              />
                            </a>
                          </div>
                        ))}
                    </div>
                  )}

                {/* Non-PDF sources — expandable */}
                {message.sender === "ai" &&
                  message.sources &&
                  message.sources.filter(
                    (s) =>
                      !(
                        (s.extension?.toLowerCase() === "pdf" ||
                          s.type === "LearningSection") &&
                        s.path &&
                        s.path !== "/"
                      )
                  ).length > 0 && (() => {
                    const nonPdfSources = message.sources.filter(
                      (s) =>
                        !(
                          (s.extension?.toLowerCase() === "pdf" ||
                            s.type === "LearningSection") &&
                          s.path &&
                          s.path !== "/"
                        )
                    );
                    return (
                      <div className="mt-3 border-t border-gray-100 pt-3">
                        <button
                          onClick={() => toggleSources(message.id)}
                          className="flex items-center text-xs text-violet-600 hover:text-violet-800 transition-colors mb-1"
                        >
                          <ArticleIcon
                            fontSize="small"
                            className="mr-1"
                            style={{ fontSize: "14px" }}
                          />
                          <span>
                            {nonPdfSources.length} source
                            {nonPdfSources.length > 1 ? "s" : ""}
                          </span>
                          {expandedSources[message.id] ? (
                            <ExpandLessIcon
                              fontSize="small"
                              className="ml-1"
                              style={{ fontSize: "14px" }}
                            />
                          ) : (
                            <ExpandMoreIcon
                              fontSize="small"
                              className="ml-1"
                              style={{ fontSize: "14px" }}
                            />
                          )}
                        </button>

                        {expandedSources[message.id] && (
                          <div className="mt-2 bg-gray-50 rounded p-3 text-xs">
                            {nonPdfSources.map((source, index) => (
                              <div
                                key={source.id}
                                className={`${
                                  index > 0
                                    ? "mt-2 pt-3 pb-2 border-t border-gray-100"
                                    : ""
                                }`}
                              >
                                <a
                                  href={source.path}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex space-x-2 hover:bg-gray-100 p-1 rounded transition-colors"
                                >
                                  <div className="rounded w-5 h-6 flex items-center justify-center border border-gray-200 bg-white">
                                    <SubjectIcon
                                      style={{ fontSize: "16px" }}
                                      className="text-gray-500"
                                    />
                                  </div>
                                  <div className="flex-1">
                                    <div className="font-medium text-gray-700">
                                      {source.title}
                                    </div>
                                    {source.fileSize && (
                                      <div className="text-gray-400 mt-1.5">
                                        {source.fileSize}
                                      </div>
                                    )}
                                  </div>
                                </a>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })()}

                <span
                  className={`text-xs self-end mt-1 ${
                    message.sender === "user"
                      ? "text-gray-500"
                      : "text-gray-400"
                  }`}
                >
                  {formatTimestamp(message.timestamp)}
                </span>
              </div>
            </div>
          </div>
        ))}

        <div ref={messagesEndRef} />
      </div>

      {/* Controls footer */}
      <div className="bg-white border-t p-4 flex items-center justify-between">
        {/* Mute/unmute button */}
        <button
          onClick={handleToggleMute}
          disabled={!isConnected}
          className={`p-3 rounded-full transition-colors ${
            isConnected
              ? isMuted
                ? "bg-red-100 text-red-600"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              : "bg-gray-100 text-gray-300 cursor-not-allowed"
          }`}
        >
          {isMuted ? (
            <MicOffIcon fontSize="medium" />
          ) : (
            <MicIcon fontSize="medium" />
          )}
        </button>

        {/* Main action button */}
        <div className="flex flex-col items-center">
          {isConnected ? (
            <button
              onClick={handleDisconnect}
              className="p-5 rounded-full bg-red-500 text-white hover:bg-red-600 transition-colors"
            >
              <StopIcon style={{ fontSize: 32 }} />
            </button>
          ) : isConnecting ? (
            <button
              disabled
              className="p-5 rounded-full bg-violet-400 text-white cursor-not-allowed"
            >
              <CircularProgress
                style={{ fontSize: 32 }}
                className="animate-spin"
              />
            </button>
          ) : (
            <button
              onClick={handleConnect}
              className="p-5 rounded-full bg-violet-600 text-white hover:bg-violet-700 transition-colors"
            >
              <MicIcon style={{ fontSize: 32 }} />
            </button>
          )}

          {/* Status text */}
          <div className="flex items-center mt-2 gap-1.5">
            <div
              className={`w-2 h-2 rounded-full ${getStatusColor()} ${
                isConnected && activityState !== ActivityState.IDLE
                  ? "animate-pulse"
                  : ""
              }`}
            />
            <span className="text-xs text-gray-500">{getStatusText()}</span>
          </div>
        </div>

        {/* Spacer to balance layout */}
        <div className="w-12" />
      </div>
    </div>
  );
}
